// MIGRATED: Now uses @vis.gl/react-google-maps for AdvancedMarker support
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  StyleSheet,
  Text,
  View,
  Platform,
  Dimensions,
  ActivityIndicator,
} from 'react-native';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ChargerAttributes } from '../../models/charger-model';
import * as Location from 'expo-location';
import { notificationService } from '../../services/notification-service';
import { getUserLocation } from '../../components/common/utils/get-user-location';

// NEW: Import from @vis.gl/react-google-maps
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
} from '@vis.gl/react-google-maps';

// Import the icon assets
import AvailableChargerIcon from '../../../assets/ChargerAvailableIcon.png';
import UnAvailableChargerIcon from '../../../assets/ChargerUnavailableIcon.png';
import SelectedChargerIcon from '../../../assets/ChargerSelectedIcon.png';
import currentLocationIcon from '../../../assets/currentLocation.png';

const { width, height } = Dimensions.get('window');

const defaultLatitude = 39.7392;
const defaultLongitude = -104.9903;

const initialCoordinates: Location.LocationObjectCoords = {
  latitude: 39.7392, // Default to Denver, CO
  longitude: -104.9903,
  accuracy: 0,
  altitude: 0,
  altitudeAccuracy: 0,
  heading: 0,
  speed: 0,
};

interface MapScreenWebProps {
  markerOpenBottomSheet: (charger: ChargerAttributes) => void;
  markerCloseBottomSheet: () => void;
  myInputLocation: Location.LocationObjectCoords;
  selectCharger: any;
  onMapInteraction: (interact: boolean) => void;
}

// TODO: Replace with your actual Google Cloud Map ID for advanced markers
const MAP_ID = process.env.REACT_NATIVE_GOOGLE_MAP_ID;

// Check if WebGL is supported for Vector maps
const isWebGLSupported = () => {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
};

export default React.memo(function MapScreen(props: MapScreenWebProps) {
  const [origin, setOrigin] = useState<Location.LocationObjectCoords | null>({
    ...props.myInputLocation,
    latitude: props.myInputLocation?.latitude ?? initialCoordinates.latitude,
    longitude: props.myInputLocation?.longitude ?? initialCoordinates.longitude,
  });
  const [destination, setDestination] =
    useState<Location.LocationObjectCoords | null>({
      ...props.myInputLocation,
      latitude: props.myInputLocation?.latitude ?? defaultLatitude,
      longitude: props.myInputLocation?.longitude,
    });
  const [selectedCharger, setSelectedCharger] = useState<any>();
  const [isMapInteraction, setIsMapInteraction] = useState(false);
  const [myLocation, setMyLocation] =
    React.useState<Location.LocationObjectCoords | null>(props.myInputLocation);

  const [myCurrentLocation, setMyCurrentLocation] =
    React.useState<Location.LocationObjectCoords>(props.myInputLocation);

  // State to track if we should use Vector or Raster map
  const [useVectorMap, setUseVectorMap] = useState(isWebGLSupported());

  // Ref to track if we've already fetched location to prevent infinite re-renders
  const hasFetchedLocation = React.useRef(false);

  const chargerList: ChargerAttributes[] = useSelector(
    (state: RootState) => state.chargers.chargers
  ) as ChargerAttributes[];

  useEffect(() => {
    // Only fetch location if we haven't fetched it yet or if the props location is different
    const shouldFetchLocation =
      !hasFetchedLocation.current ||
      myCurrentLocation.latitude !== props.myInputLocation.latitude ||
      myCurrentLocation.longitude !== props.myInputLocation.longitude;

    if (shouldFetchLocation) {
      hasFetchedLocation.current = true;
      const getLocation = async () => {
        const userLocation = await getUserLocation();
        setMyCurrentLocation((prevLocation) => {
          if (
            prevLocation &&
            prevLocation.latitude === userLocation.latitude &&
            prevLocation.longitude === userLocation.longitude &&
            prevLocation.accuracy === userLocation.accuracy
          ) {
            return prevLocation;
          }
          if (userLocation.accuracy === 0) {
            notificationService.sendNotification(
              'info',
              'No location info given!'
            );
          }
          return userLocation;
        });
      };
      getLocation();
    }
  }, [props.myInputLocation.latitude, props.myInputLocation.longitude]);

  useEffect(() => {
    if (!myLocation) return;
    setOrigin(myLocation);
    setDestination(myLocation);
  }, [myLocation]);

  const filteredChargers = useMemo(() => {
    const validChargers = chargerList?.filter(
      (charger) =>
        charger.lat !== null &&
        charger.lng !== null &&
        charger.is_enabled === true
    );
    if (Array.isArray(validChargers) && validChargers.length > 0) {
      return validChargers.map((selectCharger) => {
        if (selectCharger.uuid === selectedCharger?.uuid) {
          return { ...selectCharger, isSelected: props?.selectCharger };
        }
        return { ...selectCharger, isSelected: false };
      });
    }
    return [];
  }, [chargerList, props?.selectCharger, selectedCharger]);

  const handleMarkerPress = useCallback(
    (charger: ChargerAttributes) => {
      if (
        !(
          charger.lat === props.myInputLocation?.latitude &&
          charger.lng === props.myInputLocation?.longitude
        )
      ) {
        props?.markerOpenBottomSheet(charger);
      }
      setSelectedCharger(charger);
    },
    [props.myInputLocation, props.markerOpenBottomSheet]
  );

  // Memoize icon selection to prevent unnecessary re-computations
  const getChargerIcon = useCallback((charger: ChargerAttributes) => {
    if (charger?.isSelected === true) {
      return SelectedChargerIcon;
    } else if (charger?.isOccupied !== 1 && charger.is_enabled) {
      return AvailableChargerIcon;
    } else {
      return UnAvailableChargerIcon;
    }
  }, []);

  // AdvancedMarker elements
  const markerElements = useMemo(() => {
    return filteredChargers.map((charger) => {
      return (
        <AdvancedMarker
          key={charger.uuid}
          position={{ lat: charger.lat!, lng: charger.lng! }}
          title={charger.name}
          onClick={() => handleMarkerPress(charger)}
          zIndex={10} // Higher zIndex to appear above user location
        >
          <img
            src={getChargerIcon(charger)}
            width={32}
            height={32}
            alt={charger.name}
          />
        </AdvancedMarker>
      );
    });
  }, [filteredChargers, handleMarkerPress, getChargerIcon]);

  // User location marker
  const userLocationMarker = useMemo(() => {
    if (props.myInputLocation.accuracy === 0) {
      return null;
    }
    return (
      <AdvancedMarker
        key="myLocationMarker"
        position={{
          lat: props.myInputLocation?.latitude ?? 0,
          lng: props.myInputLocation?.longitude ?? 0,
        }}
        title="Current Location"
        zIndex={1}
      >
        <img
          src={currentLocationIcon}
          width={32}
          height={32}
          alt="Current Location"
        />
      </AdvancedMarker>
    );
  }, [props.myInputLocation]);

  const mapCenter = useMemo(
    () => ({
      lat: myCurrentLocation.latitude,
      lng: myCurrentLocation.longitude,
    }),
    [myCurrentLocation]
  );

  const handleMapInteraction = useCallback(() => {
    setIsMapInteraction(true);
    props.onMapInteraction(true);
    setTimeout(() => {
      setIsMapInteraction(false);
    }, 100);
  }, [props.onMapInteraction]);

  // // Log when falling back to Raster map
  // useEffect(() => {
  //   if (!useVectorMap) {
  //     console.log(
  //       'Using Raster map (Vector map not supported or failed to load)'
  //     );
  //   }
  // }, [useVectorMap]);

  // TODO: Add error handling for API key and mapId
  return (
    <View style={{ flex: 1, width: '100%', height: '100%' }}>
      <APIProvider apiKey={process.env.REACT_NATIVE_GOOGLE_MAPS_API_KEY!}>
        <Map
          style={{ flex: 1, width: '100%', height: '100%' }}
          defaultCenter={{
            lat: myCurrentLocation?.latitude ?? defaultLatitude,
            lng: myCurrentLocation?.longitude ?? defaultLongitude,
          }}
          defaultZoom={12}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeId={useVectorMap ? 'roadmap' : 'roadmap'}
        >
          {markerElements}
          {userLocationMarker}
        </Map>
      </APIProvider>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
  },
  map: {
    ...StyleSheet.absoluteFillObject,
    borderColor: 'white',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0B1E1D',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  markerContainer: {
    width: 40,
    height: 40,
  },
  markerImage: {
    flex: 1,
    width: undefined,
    height: undefined,
  },
  rgnText: {
    fontSize: 12,
    color: '#666666',
  },
  rgnView: { flexDirection: 'row', alignItems: 'flex-end' },
  button: {
    backgroundColor: 'orange',
    padding: 8,
    margin: 5,
    borderRadius: 5,
    alignItems: 'center',
    height: 35,
  },
  buttonText: {
    color: 'white',
    textAlign: 'center',
    fontWeight: 'bold',
  },
});
